import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Skeleton, StatusTag } from '@/components/ui';
import type { OrgAdminRow, Organization, OrgTenant } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';
import { formatCountVN } from './orgFormat';

interface OrganizationPeekDrawerProps {
    org: Organization | null;
    tenants: OrgTenant[] | undefined;
    tenantsLoading: boolean;
    admins: OrgAdminRow[] | undefined;
    adminsLoading: boolean;
    accountCount: number | undefined;
    onClose: () => void;
    onOpenDetail: (org: Organization) => void;
    onNavigateTab: (org: Organization, tab: 'tenants' | 'admins' | 'branding') => void;
    onCreateTenant: (org: Organization) => void;
}

/**
 * EAA-1815: the list's "quick peek" — frame 21 lines 750-1049 (the 400px
 * aside). A `Drawer` (antd, right placement) rather than a permanent aside:
 * "detail is a full page … the drawer survives only as a quick peek from the
 * list" (UX-SPEC). Only "Tổng quan" renders content here; the other three
 * tabs are direct links into the full detail page's own tabs (URL-backed
 * there) rather than a second copy of that content living in the drawer too.
 */
export function OrganizationPeekDrawer({
    org,
    tenants,
    tenantsLoading,
    admins,
    adminsLoading,
    accountCount,
    onClose,
    onOpenDetail,
    onNavigateTab,
    onCreateTenant,
}: OrganizationPeekDrawerProps) {
    const { t } = useTranslation();
    const tenantStatusConfig = useMemo(
        () => ({
            active: { label: t('admin.organizations.status.active'), tone: 'success' as const },
            inactive: {
                label: t('admin.organizations.statusTabs.inactive'),
                tone: 'default' as const,
            },
        }),
        [t]
    );

    if (!org) return null;

    const tenantCount = tenants?.length;
    const brandColor = org.branding?.primary_color;

    return (
        <Drawer
            open={Boolean(org)}
            onClose={onClose}
            width={400}
            closeIcon={false}
            title={
                <span className="portal-org-peek-header">
                    <OrgAvatar name={org.name} size={40} tone="accent" />
                    <span className="portal-org-peek-identity">
                        <span className="portal-org-peek-name">{org.name}</span>
                        <code className="portal-org-peek-code">{org.code}</code>
                    </span>
                    <button
                        type="button"
                        style={{
                            flex: 'none',
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            color: 'var(--color-text-muted)',
                            cursor: 'pointer',
                        }}
                        aria-label={t('common.close')}
                        onClick={onClose}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                        >
                            <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                        </svg>
                    </button>
                </span>
            }
            styles={{ body: { display: 'flex', flexDirection: 'column', gap: 0, padding: 0 } }}
            footer={
                <div className="portal-org-peek-footer">
                    <button
                        type="button"
                        className="portal-btn portal-btn-primary"
                        style={{ flex: 1 }}
                        onClick={() => onOpenDetail(org)}
                    >
                        {t('admin.organizations.openOrgPage')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn"
                        onClick={() => onCreateTenant(org)}
                    >
                        {t('admin.organizations.addTenantAction')}
                    </button>
                </div>
            }
        >
            <div className="portal-org-peek-tabs">
                <span className="portal-org-peek-tab is-active">
                    {t('admin.organizations.detail.tabs.overview')}
                </span>
                <button
                    type="button"
                    className="portal-org-peek-tab"
                    onClick={() => onNavigateTab(org, 'tenants')}
                >
                    {t('admin.organizations.tenantsLabel')}
                </button>
                <button
                    type="button"
                    className="portal-org-peek-tab"
                    onClick={() => onNavigateTab(org, 'admins')}
                >
                    {t('admin.organizations.detail.tabs.admins')}
                </button>
                <button
                    type="button"
                    className="portal-org-peek-tab"
                    onClick={() => onNavigateTab(org, 'branding')}
                >
                    {t('admin.organizations.detail.tabs.branding')}
                </button>
            </div>

            <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div className="portal-org-peek-stats">
                    <div className="portal-org-peek-stat">
                        <span className="portal-org-peek-stat-label">
                            {t('admin.organizations.tenantsLabel')}
                        </span>
                        <span className="portal-org-peek-stat-num">
                            {tenantCount === undefined
                                ? t('admin.organizations.pendingValuePlaceholder')
                                : formatCountVN(tenantCount)}
                        </span>
                    </div>
                    <div className="portal-org-peek-stat">
                        <span className="portal-org-peek-stat-label">
                            {t('admin.organizations.detail.tabs.admins')}
                        </span>
                        <span className="portal-org-peek-stat-num">
                            {adminsLoading || admins === undefined
                                ? t('admin.organizations.pendingValuePlaceholder')
                                : formatCountVN(admins.length)}
                        </span>
                    </div>
                </div>

                {accountCount !== undefined && (
                    <p className="portal-org-aside-hint" style={{ margin: 0 }}>
                        {t('admin.organizations.peekDrawer.accountsInOrgHint', {
                            value: formatCountVN(accountCount),
                        })}
                    </p>
                )}

                <div className="portal-org-peek-section">
                    <span className="portal-org-peek-section-label">
                        {t('admin.organizations.tenantsLabel')}
                    </span>
                    {tenantsLoading && (
                        <div role="status" aria-live="polite">
                            <span className="portal-sr-only">
                                {t('admin.organizations.loadingSrOnly')}
                            </span>
                            <Skeleton height="2.5rem" />
                        </div>
                    )}
                    {!tenantsLoading && tenantCount === 0 && (
                        <p className="portal-org-notenant-callout">
                            {t('admin.organizations.peekDrawer.noTenantShort')}
                        </p>
                    )}
                    {!tenantsLoading &&
                        (tenants ?? []).slice(0, 4).map((tenant) => (
                            <div className="portal-org-peek-tenant-row" key={tenant.id}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span className="portal-org-peek-tenant-name">
                                        {tenant.name ?? tenant.tenant_id}
                                    </span>
                                    <br />
                                    <span className="portal-org-peek-tenant-slug">
                                        {tenant.slug}
                                    </span>
                                </span>
                                <StatusTag
                                    status={tenant.tenant_is_active ? 'active' : 'inactive'}
                                    config={tenantStatusConfig}
                                />
                            </div>
                        ))}
                </div>

                <div className="portal-org-peek-section">
                    <span className="portal-org-peek-section-label">
                        {t('admin.organizations.peekDrawer.brandingSectionLabel')}
                    </span>
                    <div className="portal-org-peek-brand-row">
                        <span
                            className="portal-org-peek-brand-swatch"
                            style={{ background: brandColor || 'var(--color-primary)' }}
                            aria-hidden="true"
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <code style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13 }}>
                                {brandColor
                                    ? brandColor.toUpperCase()
                                    : t('admin.organizations.defaultBrandLabel')}
                            </code>
                            <br />
                            <span className="portal-org-aside-hint">
                                {t('admin.organizations.peekDrawer.brandingApplyHint')}
                            </span>
                        </span>
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={() => onNavigateTab(org, 'branding')}
                        >
                            {t('common.edit')}
                        </button>
                    </div>
                </div>
            </div>
        </Drawer>
    );
}

export default OrganizationPeekDrawer;
